import { SparkleIcon } from "assets/icons/Sparkle";
import { TrendingDownIcon } from "assets/icons/TrendingDown";
import { orderTypeConstants } from "constants/order";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import { ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  IOrder,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import { StyledEstimateResults } from "./styled";

interface IEstimateFigure {
  label: string;
  icon: ReactNode;
  value?: number | null;
}

interface IBreakdownRow {
  label: string;
  value?: number | null;
  prefix?: string;
}

// previousOrder: the sent order, passed by Redo (CLE-2890). The saved field mirrors the headline:
// amountInCents, or the patient-balance estimate for patient-balance orders.
export function EstimateResults({
  previousOrder = null,
}: {
  previousOrder?: Pick<IOrder, "amountInCents" | "serviceDetails"> | null;
}) {
  const [expanded, setExpanded] = useState(false);

  const { watch } = useFormContext();
  const { loadingPriceBreakdown } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );

  const isLoading = loadingPriceBreakdown || !!watch("isDiscountLoading");

  const orderType = watch("orderType");
  const isPatientBalanceOrder =
    orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
  const isDefaultOrder =
    orderType !== orderTypeConstants.GFE && !isPatientBalanceOrder;

  const isSelfPayRate = watch("payerName") === "Self-Pay Rate";

  const patientResponsibility = watch(
    isPatientBalanceOrder ? "amount" : "totalAmount"
  );
  const previousAmountInCents = isPatientBalanceOrder
    ? (
        previousOrder?.serviceDetails as
          | IPatientResponsibilityServiceDetails
          | undefined
      )?.patientResponsibilityEstimateAmountInCents ?? null
    : previousOrder?.amountInCents ?? null;

  const insurancePays = watch("totalInsurancePortion");
  const contractedAllowable = watch("totalInsuranceAllowable");
  const deductible = watch("totalDeductible");
  const coinsurance = watch("totalCoinsurance");
  const copay = watch("totalCopay");
  const totalPatientEstimate = watch("amount");
  const clearPatientFee = watch("clearPatientFee");
  const clearPatientFeePercentage = watch("clearPatientFeePercentage");
  const discountPercent = watch("discountPercent");
  const discountAmount = watch("discountAmount");

  const showPreviousAmount =
    !isLoading &&
    previousAmountInCents != null &&
    patientResponsibility != null &&
    // The patient-balance section re-derives the amount from a percent rounded to two
    // decimals, so one cent of drift is not a change.
    Math.abs(dollarsToCents(patientResponsibility) - previousAmountInCents) > 1;

  const patientBalanceEstimate = watch("totalAmount");
  const promptPayDiscount =
    patientBalanceEstimate != null && patientResponsibility != null
      ? patientBalanceEstimate - patientResponsibility
      : null;

  const facilityEstimate = watch("estimatedVisitCostForPatient");
  const bundledPrice = watch("amount");
  const youSave =
    facilityEstimate != null && patientResponsibility != null
      ? facilityEstimate - patientResponsibility
      : null;

  const pickForOrderType = <T,>(configs: {
    gfe: T;
    clearPackage: T;
    patientBalance: T;
  }): T => {
    if (isPatientBalanceOrder) return configs.patientBalance;
    if (isDefaultOrder) return configs.clearPackage;
    return configs.gfe;
  };

  const figures = pickForOrderType<IEstimateFigure[]>({
    gfe: [
      ...(isSelfPayRate
        ? []
        : [
            {
              label: "Contracted Rate",
              icon: <i className="pi pi-building" />,
              value: contractedAllowable,
            },
          ]),
      {
        label: "Insurance Pays",
        icon: <i className="pi pi-shield" />,
        value: insurancePays,
      },
    ],
    clearPackage: [
      {
        label: "Estimated Payment Due",
        icon: <i className="pi pi-building" />,
        value: facilityEstimate,
      },
      {
        label: "You Save",
        icon: <TrendingDownIcon />,
        value: youSave,
      },
    ],
    patientBalance: [
      {
        label: "Prompt-Pay Discount",
        icon: <TrendingDownIcon />,
        value: promptPayDiscount,
      },
    ],
  });

  const breakdownRows = pickForOrderType<IBreakdownRow[]>({
    gfe: [
      { label: "Applied to deductible", value: deductible },
      { label: "Coinsurance", value: coinsurance },
      { label: "Copay", value: copay },
      {
        label:
          clearPatientFee > 0
            ? "Subtotal Patient Estimate"
            : "Total Patient Estimate",
        value: totalPatientEstimate,
      },
    ],
    clearPackage: [
      { label: "Estimated Payment Due", value: facilityEstimate },
      { label: "Bundled Price", value: bundledPrice },
      { label: "You Save", value: youSave },
    ],
    patientBalance: [
      { label: "Total Patient Estimate", value: patientBalanceEstimate },
      ...(patientBalanceEstimate
        ? [
            {
              label: `Discount (${discountPercent ?? 0}%)`,
              value: promptPayDiscount,
              prefix: "−",
            },
          ]
        : []),
    ],
  });

  return (
    <StyledEstimateResults data-testid="estimateResults">
      <div className="results-head">
        <span className="results-eyebrow">
          <SparkleIcon width={16} height={16} />
          Estimate Results
        </span>

        <p className="results-label mt-3 mb-3">Patient Responsibility</p>
        {(() => {
          if (isLoading) {
            return <span className="results-skeleton is-headline" />;
          }
          return patientResponsibility != null ? (
            <p className="results-headline">
              {parsePrice(patientResponsibility)}
            </p>
          ) : (
            <span className="results-placeholder" />
          );
        })()}
        {showPreviousAmount && (
          <p
            className="results-label mt-2 mb-0"
            data-testid="estimateResults-previousAmount"
          >
            Previously {parsePrice(centsToDollars(previousAmountInCents))}
          </p>
        )}

        {isLoading && (
          <p className="results-label line-height-3 mt-3 mb-0">
            Calculating the estimate…
          </p>
        )}
        {!isLoading && patientResponsibility == null && (
          <p className="results-label line-height-3 mt-3 mb-0">
            Complete benefits and add services to generate the estimate.
          </p>
        )}
      </div>

      <div className="results-figures">
        {figures.map(({ label, icon, value }) => (
          <div className="results-figure" key={label}>
            <span className="results-label results-figure-label flex align-items-center gap-2">
              {icon}
              {label}
            </span>
            {(() => {
              if (isLoading) {
                return <span className="results-skeleton is-figure mt-3" />;
              }
              return value != null ? (
                <p className="results-figure-value">{parsePrice(value)}</p>
              ) : (
                <span className="results-placeholder is-small mt-3" />
              );
            })()}
          </div>
        ))}
      </div>

      <div className="results-breakdown">
        <button
          type="button"
          className="results-breakdown-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          Estimate breakdown
          <i
            className={expanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            style={{ color: "var(--brand-gray-600)" }}
          />
        </button>

        {expanded && (
          <>
            {breakdownRows.map(({ label, value, prefix }) => (
              <div className="results-row" key={label}>
                <span className="results-label">{label}</span>
                {(() => {
                  if (isLoading) {
                    return <span className="results-skeleton is-row" />;
                  }
                  return value != null ? (
                    <span className="results-row-value">
                      {Math.round(value * 100) !== 0 && prefix}
                      {parsePrice(value)}
                    </span>
                  ) : (
                    <span className="results-placeholder is-row" />
                  );
                })()}
              </div>
            ))}

            {!isDefaultOrder &&
              !isPatientBalanceOrder &&
              discountPercent > 0 && (
                <div className="results-row">
                  <span className="results-label">
                    Discount ({discountPercent}%)
                  </span>
                  {(() => {
                    if (isLoading) {
                      return <span className="results-skeleton is-row" />;
                    }
                    return discountAmount != null ? (
                      <span className="results-row-value">
                        {Math.round(discountAmount * 100) !== 0 && "−"}
                        {parsePrice(discountAmount)}
                      </span>
                    ) : (
                      <span className="results-placeholder is-row" />
                    );
                  })()}
                </div>
              )}

            {!isDefaultOrder &&
              !isPatientBalanceOrder &&
              clearPatientFee > 0 && (
                <div className="results-row">
                  <span className="results-label">
                    Clear Patient Fee
                    {clearPatientFeePercentage > 0
                      ? ` (${clearPatientFeePercentage}%)`
                      : ""}
                  </span>
                  {isLoading ? (
                    <span className="results-skeleton is-row" />
                  ) : (
                    <span className="results-row-value">
                      +{parsePrice(clearPatientFee)}
                    </span>
                  )}
                </div>
              )}

            <div className="results-total">
              Patient Responsibility
              {(() => {
                if (isLoading) {
                  return <span className="results-skeleton is-row" />;
                }
                return patientResponsibility != null ? (
                  <span>{parsePrice(patientResponsibility)}</span>
                ) : (
                  <span className="results-placeholder is-row" />
                );
              })()}
            </div>
          </>
        )}
      </div>
    </StyledEstimateResults>
  );
}
